/** O que a poeira lê do corpo a cada quadro (o estado replicado, ou o previsto no jogador local). */
export interface FootBody {
  readonly dashTime: number;
  readonly slideTime: number;
  readonly leaping: boolean;
  readonly grounded: boolean;
  readonly vy: number;
  readonly swimming: boolean;
}

/**
 * Um sopro de poeira nos pés (Fase 14, a referência do golpe): a arrancada (Investida, Rasteira), o pouso
 * (o fim do Salto, a queda) e o rastro enquanto o corpo corre no chão. `strength` de 0,4 a 1,4.
 */
export interface DustEvent {
  kind: "arranque" | "pouso" | "rastro";
  strength: number;
}

/** A queda mais lenta que levanta poeira (m/s) e a que dá a força inteira. */
export const DUST_FALL = { min: 4, full: 10 } as const;
/** Um sopro do rastro a cada tantos ms, enquanto a Investida ou a Rasteira correm no chão. */
export const DUST_TRAIL_MS = 60;

const NONE: readonly DustEvent[] = [];

interface State {
  dash: boolean;
  slide: boolean;
  leaping: boolean;
  grounded: boolean;
  /** A maior velocidade de queda desde que saiu do chão (m/s). */
  fall: number;
  trailAt: number;
}

/**
 * Os eventos de poeira de cada corpo (Fase 14), puros para os testes: compara o quadro com o anterior do mesmo
 * corpo. O primeiro quadro de um corpo só guarda o estado (quem entra na tela já correndo não levanta poeira
 * do nada). Na água, nada.
 */
export class FootDust {
  private readonly states = new Map<string, State>();

  frame(id: string, body: FootBody, now: number): readonly DustEvent[] {
    const dash = body.dashTime > 0;
    const slide = body.slideTime > 0;
    const next = this.states.get(id);
    if (!next) {
      this.states.set(id, { dash, slide, leaping: body.leaping, grounded: body.grounded, fall: body.grounded ? 0 : Math.max(0, -body.vy), trailAt: -Infinity });
      return NONE;
    }
    // O estado de antes, lido antes de o mesmo objeto virar o de agora (sem alocar por quadro).
    const prevDash = next.dash;
    const prevSlide = next.slide;
    const prevLeaping = next.leaping;
    const prevGrounded = next.grounded;
    const prevFall = next.fall;
    next.dash = dash;
    next.slide = slide;
    next.leaping = body.leaping;
    next.grounded = body.grounded;
    next.fall = body.grounded ? 0 : Math.max(prevFall, -body.vy);
    if (body.swimming) return NONE;
    const out: DustEvent[] = [];
    if ((dash && !prevDash) || (slide && !prevSlide)) {
      out.push({ kind: "arranque", strength: dash ? 1 : 0.8 });
      next.trailAt = now;
    }
    if (prevLeaping && !body.leaping) out.push({ kind: "pouso", strength: 1.4 });
    else if (body.grounded && !prevGrounded && prevFall >= DUST_FALL.min) out.push({ kind: "pouso", strength: Math.min(1.2, Math.max(0.4, prevFall / DUST_FALL.full)) });
    if ((dash || slide) && body.grounded && now - next.trailAt >= DUST_TRAIL_MS) {
      out.push({ kind: "rastro", strength: slide ? 0.8 : 0.6 });
      next.trailAt = now;
    }
    return out.length > 0 ? out : NONE;
  }

  /** O corpo saiu da partida. */
  remove(id: string): void {
    this.states.delete(id);
  }

  clear(): void {
    this.states.clear();
  }
}
